import type { ScheduledMessage } from 'features/scheduled-messages/types'

import user from 'assets/image/icons/svg/user.svg'
import { appSelectors } from '@appStore/reducers/app'
import clsx from 'clsx'
import { useChatScheduled } from 'features/scheduled-messages/hooks'
import { type ComponentPropsWithoutRef, useMemo } from 'react'
import { appDay } from 'shared/day'
import { useAppSelector, useUnMount } from 'shared/hooks'
import { StackIcon } from 'shared/ui'

import { Menu } from './Menu'
import css from './Messages.module.css'

const getStartDay = (message: ScheduledMessage) =>
	appDay(message.time).startOf('day').toISOString()

const groupByDate = (messages: ScheduledMessage[]) =>
	Object.groupBy(messages, getStartDay)

export function Messages({ className }: ComponentPropsWithoutRef<'div'>) {
	const { currentData } = useChatScheduled()

	const grouped = useMemo(
		() => groupByDate(currentData?.documents ?? []),
		[currentData?.documents]
	)

	const keys = useMemo(
		() =>
			Object.keys(grouped).sort((first, second) => appDay(first).diff(second)),
		[grouped]
	)

	return (
		<div className={clsx(className, css.root, 'app-scroll')}>
			{keys.map(key => (
				<section key={key}>
					<h3 className={css.day}>
						<time dateTime={key}>{appDay(key).format('D MMM')}</time>
					</h3>
					{grouped[key]?.map(message => (
						<Message key={message.id} {...message} />
					))}
				</section>
			))}

			<Menu />
		</div>
	)
}

function Message(message: ScheduledMessage) {
	const { id, text, time } = message
	const persona = useAppSelector(appSelectors.persona)
	const fullName = useAppSelector(appSelectors.fullName)

	useUnMount(() => {
		id && Menu.optionalClose(id)
	})

	return (
		<section className={css.message}>
			<img
				alt='user avatar'
				className={css.avatar}
				height={30}
				src={persona?.linkedInProfile?.pictureUrl || user}
				width={30}
			/>
			<p className={css.name}>{fullName}</p>
			<time className={css.time} dateTime={time ?? undefined}>
				{appDay(time).format('HH:mm')}
			</time>
			<blockquote className={css.text}>{text}</blockquote>

			<button
				className={css.actions}
				onClick={event => Menu.open(event, message)}
				type='button'
			>
				<StackIcon icon='dots' stack='linkedin' />
			</button>
		</section>
	)
}
